import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  SyntheticEvent,
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
  Suspense,
} from 'react';
import { Card, Header, Segment, Form, Input, Button } from 'semantic-ui-react';
import { DropResult } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';
import { useStore } from '../../../app/stores/store';
import sendRespectiveEmail from '../../../app/models/EmailNew';
import {
  errorEmptyText,
  errorMessageText,
} from '../../../app/common/form/errorMessageText';
import DraggableComponent from '../../../library/draggable';
import { ListI } from '../../../library/draggable/types';
import { DashboardConfigurationInterface } from '../../../app/models/DashboardConfiguration';
import Settings from '../settings';
import { toast } from 'react-toastify';
import { userLanguage } from '../../../app/common/userLanguageSchema/userLanguage';

const DeleteModal = React.lazy(() => import('../../../library/deleteModal'));

function DashboardLevel({
  cardClickHandler,
  levelDetails,
  parentIndex,
  isForm,
}: {
  levelDetails: {
    level: number;
    parentlevelId: string;
    selectedId: string;
    data: DashboardConfigurationInterface[];
  };
  cardClickHandler: (
    id: string,
    index: number,
    type: string,
    isDeleted?: boolean,
  ) => void;
  parentIndex: number;
  isForm: boolean;
}) {
  // -------------------- Stores ------------------------ //
  const { commonStore, userStore, DashboardConfigurationStore, modalStore } =
    useStore();

  // -------------------- Stores Destructuring ------------------------ //
  const {
    dashboardLevelDetails,
    editLevelItem,
    createDashboardAddnew,
    ReArrangeOrder,
    // getDashboardSettingDetails,
  } = DashboardConfigurationStore;
  // const { dashboard } = dashboardStore;

  const inputRef = useRef<HTMLDivElement | null>(null);

  // -------------------- States ------------------------ //
  const [itemDetails, setItemDetails] = useState({
    isEdit: false,
    id: '',
    name: '',
    index: 0,
  });
  const [newTask, setNewTask] = useState<string>('');
  const [temptasks, setTemptasks] = useState<
    {
      id: string;
      name: string;
      type: string;
      icon?: string;
    }[]
  >([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // ------------------- useLayoutEffect ------------------- //
  useLayoutEffect(() => {
    let input = document.getElementById(
      `dashboardLevelInput-${levelDetails.level}`,
    );
    if (input) {
      input.focus();
    }
  }, [levelDetails.level]);

  // -------------------- UseEffect ------------------------ //
  useEffect(() => {
    let filtered = dashboardLevelDetails?.[levelDetails.level].data?.map(
      (data: {
        levelId: string;
        name: string;
        type: string;
        icon?: string;
        flexBasis?: string;
      }) => {
        return {
          id: data?.levelId || '',
          name: data?.name || '',
          type: data?.type || '',
          icon: data?.icon || '',
          flexBasis: data?.flexBasis || '',
        };
      },
    );
    setTemptasks(filtered);
    // eslint-disable-next-line
  }, [JSON.stringify(dashboardLevelDetails?.[levelDetails.level]?.data)]);

  useEffect(() => {
    setNewTask(itemDetails.name);
  }, [itemDetails.name]);

  // -------------------- Handlers ------------------------ //
  const existNameHandler = (arr: ListI[], name: string) => {
    let x = arr?.filter((d) => d.name === name);
    return !!x?.length;
  };

  const handleChangeName = (_e: SyntheticEvent, data: any) => {
    setErrorMessage('');
    setNewTask(data.value);
  };

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      if (itemDetails.isEdit) {
        if (
          commonStore.filterModeTwo.includes('DYDC-SAVE') ||
          commonStore.filterModeZero.includes('DYDC-SAVE')
        ) {
          toast.info('You are not permitted to edit.');
          return;
        }
        SavendCancelHandler('save');
      } else {
        if (
          commonStore.filterModeTwo.includes('DYDC-CREATE') ||
          commonStore.filterModeZero.includes('DYDC-CREATE')
        ) {
          toast.info('You are not permitted to create.');
          return;
        }
        handleAddTasks();
      }
    }
  };

  const handleAddTasks = () => {
    if (existNameHandler(temptasks, newTask)) {
      setErrorMessage(errorMessageText.content);
      return;
    }
    if (newTask?.length === 0) {
      setErrorMessage(errorEmptyText.content);
      return;
    }
    setErrorMessage('');
    if (temptasks) {
      const newElement = {
        levelId: uuid(),
        levelIndex: temptasks.length,
        name: newTask,
        customerName: userStore?.user?.customerName as string,
        creatorName: userStore?.user?.displayName as string,
        dateTime: commonStore.updateDateTime(),
        templateLevelId: levelDetails.parentlevelId,
        index:
          DashboardConfigurationStore?.dashboardLevelDetails?.[
            levelDetails.level
          ]?.data?.length - 1,
        type: '',
        icon: '',
      };
      temptasks.push({
        id: newElement?.levelId || '',
        name: newElement?.name || '',
        type: newElement?.type || '',
        icon: newElement?.icon || '',
      });
      setTemptasks(temptasks);
      createDashboardAddnew(newElement);

      if (
        DashboardConfigurationStore?.dashboardLevelDetails?.[levelDetails.level]
          ?.data
      )
        DashboardConfigurationStore.dashboardLevelDetails[
          levelDetails.level
        ].data.push(newElement);

      sendRespectiveEmail('created', newElement.name, 'Created Task', ''); //email Function
    } else {
      setErrorMessage('EmptyName');
    }
    setNewTask('');
  };

  // const handleClone = () => {
  //   setNewTask("");
  // };

  const onDragEnd = async (result: DropResult) => {
    const { draggableId, destination, source } = result;

    if (destination?.index === source.index) return;

    if (temptasks?.length > 0 && destination !== null) {
      ReArrangeOrder(
        draggableId,
        levelDetails.parentlevelId,
        destination?.index as number,
      );
      let copytempTasks = [...temptasks];
      copytempTasks.splice(source.index, 1);
      copytempTasks.splice(
        destination?.index as number,
        0,
        temptasks[source.index],
      );
      let copyData = [...levelDetails.data];
      copyData.splice(source.index, 1);
      copyData.splice(
        destination?.index as number,
        0,
        levelDetails.data[source.index],
      );

      if (DashboardConfigurationStore?.dashboardLevelDetails?.[parentIndex]) {
        DashboardConfigurationStore.dashboardLevelDetails[parentIndex] = {
          ...DashboardConfigurationStore.dashboardLevelDetails[parentIndex],
          data: copyData,
        };
      }
      setTemptasks(copytempTasks);
    }
  };

  const editHandler = (data: any, index: number) => {
    let input = document.getElementById(
      `dashboardLevelInput-${levelDetails.level}`,
    ) as HTMLInputElement;
    if (input) {
      input.focus();
    }
    setItemDetails((p) => ({
      id: data.id,
      name: p.isEdit ? '' : data.name,
      isEdit: !p.isEdit,
      index,
    }));
  };

  const cardHandler = (
    id: string,
    index: number,
    type: string,
    isDeleted?: boolean,
  ) => {
    if ((levelDetails.selectedId === id || isForm) && !isDeleted) return;

    if (isDeleted) {
      DashboardConfigurationStore.deleteModalDetails = {
        open: !!isDeleted,
        id,
        index,
        type,
        isDeleted: !!isDeleted,
        isDeleteLoading: false,
      };
    } else {
      cardClickHandler(id, index, type, isDeleted);
    }
  };

  const deleteModalCancel = () => {
    DashboardConfigurationStore.deleteModalDetails = {
      id: '',
      type: '',
      index: 0,
      isDeleted: false,
      open: false,
      isDeleteLoading: false,
    };
  };

  const SavendCancelHandler = (type?: string) => {
    if (type === 'save') {
      let copy = [...temptasks];
      editLevelItem(itemDetails.id, newTask);
      if (copy[itemDetails.index]) copy[itemDetails.index].name = newTask;
    }
    setItemDetails({
      id: '',
      name: '',
      isEdit: false,
      index: 0,
    });
    setNewTask('');
  };

  const settingsHandler = async (data: any, _index: number) => {
    DashboardConfigurationStore.isSettingLoading = true;
    modalStore.modal.open = true;
    modalStore.modal.body = (
      <Settings
        indexOfLevel={levelDetails.level}
        levelId={data?.id || ''}
        name={data?.name || ''}
        defaultType={data?.type || ''}
        defaultIcon={data?.icon || ''}
        defaultFlexBasis={data?.flexBasis || '0'}
      />
    );
  };

  const RenderWithMemo = () =>
    useMemo(
      () => (
        <Card
          style={{
            display: 'inline-block',
            padding: '10px',
            height: `calc(100% - ${levelDetails.level * 2}%)`,
            width: '200pt',
            marginLeft: '20px',
            marginTop: '5px',
            color: 'black',
            borderRadius: '10px',
            boxShadow: 'rgba(0, 0, 0, 0.09) 0px 3px 12px',
            transition: '.3s all',
          }}
        >
          <Header
            style={{ padding: '10px 0' }}
            size="huge"
            as="h1"
            content={`Level ${levelDetails.level + 1}`}
            sub
            color={'grey'}
          />
          <>
            <DraggableComponent
              droppableId="level"
              className="dashboardLevelWrapper"
              cardClickHandler={(
                id: string,
                index: number,
                type: string,
                isDeleted?: boolean,
              ) => cardHandler(id, index, type, isDeleted)}
              editHandler={editHandler}
              onDragEndHandler={onDragEnd}
              settingsHandler={settingsHandler}
              list={temptasks}
            />
            <Segment className="workflow-bottom-card">
              <Form>
                <Form.Group widths="equal">
                  <Form.Field
                    id={`dashboardLevelInput-${levelDetails.level}`}
                    control={Input}
                    ref={inputRef}
                    className="workflow-input"
                    error={errorMessage || null}
                    value={newTask}
                    placeholder={
                      (userLanguage[0].RPModal.RP03 as any)?.[
                        `${commonStore.language.key}`
                      ]
                    }
                    onChange={handleChangeName}
                    onKeyDown={handleKeyPress}
                  />
                </Form.Group>
              </Form>
              {!itemDetails.isEdit ? (
                !commonStore.filterModeTwo.includes('DYDC-CREATE') && (
                  <Button
                    style={{ width: '100%' }}
                    color="green"
                    floated="right"
                    onClick={handleAddTasks}
                    className="workflow-input-button"
                    disabled={
                      commonStore.filterModeZero.includes('DYDC-CREATE') ||
                      !!errorMessage
                    }
                  >
                      {
                         (userLanguage[0].RPModal.RP02 as any)?.[
                             `${commonStore.language.key}`
                          ]
                      }
                  </Button>
                )
              ) : (
                <div className="workflow-input-div">
                  {!commonStore.filterModeTwo.includes('DYDC-CANCEL') && (
                    <Button
                      secondary
                      onClick={() => SavendCancelHandler()}
                      className="workflow-input-button"
                      disabled={commonStore.filterModeZero.includes(
                        'DYDC-CANCEL',
                      )}
                    >
                      Cancel
                    </Button>
                  )}
                  {!commonStore.filterModeTwo.includes('DYDC-SAVE') && (
                    <Button
                      primary
                      floated="right"
                      disabled={
                        commonStore.filterModeZero.includes('DYDC-SAVE') ||
                        !!errorMessage
                      }
                      onClick={() => SavendCancelHandler('save')}
                      className="workflow-input-button"
                    >
                      Save
                    </Button>
                  )}
                </div>
              )}
            </Segment>
          </>
        </Card>
      ),
      // eslint-disable-next-line
      [
        levelDetails,
        temptasks,
        itemDetails.isEdit,
        commonStore.filterModeTwo,
        newTask,
        errorMessage,
      ],
    );

  return (
    <>
      {DashboardConfigurationStore.deleteModalDetails.open && (
        <Suspense fallback={<></>}>
          <DeleteModal
            isLoading={
              DashboardConfigurationStore.deleteModalDetails.isDeleteLoading
            }
            isModalOpen={DashboardConfigurationStore.deleteModalDetails.open}
            closeHandler={deleteModalCancel}
            deleteHandler={() => {
              DashboardConfigurationStore.deleteModalDetails = {
                ...DashboardConfigurationStore.deleteModalDetails,
                isDeleteLoading: true,
              };
              cardClickHandler(
                DashboardConfigurationStore.deleteModalDetails.id,
                DashboardConfigurationStore.deleteModalDetails.index,
                DashboardConfigurationStore.deleteModalDetails.type,
                DashboardConfigurationStore.deleteModalDetails.isDeleted,
              );
            }}
            header={'Delete'}
            text={'Are you sure you want to delete this?'}
          />
        </Suspense>
      )}
      {RenderWithMemo()}
    </>
  );
}

export default observer(DashboardLevel);
